import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  ErrorBar,
  Line,
  LineChart,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatCompact, formatNumber, formatPercent, formatPrice, formatUtcTick } from "@/lib/formatters";

const palette = [
  "hsl(190 90% 58%)",
  "hsl(146 90% 44%)",
  "hsl(27 85% 63%)",
  "hsl(340 84% 61%)",
  "hsl(48 95% 55%)",
  "hsl(271 88% 64%)",
  "hsl(207 88% 61%)",
  "hsl(8 82% 64%)",
];

const axisTickStyle = { fontSize: 11, fill: "var(--foreground)" };
const xAxisProps = {
  tick: axisTickStyle,
  axisLine: { stroke: "var(--border)", strokeOpacity: 0.8 },
  tickLine: { stroke: "var(--border)", strokeOpacity: 0.8 },
};

type TooltipEntry = {
  label: string;
  value: string;
  color?: string;
};

type RechartsTooltipPayload = {
  name?: string | number;
  value?: unknown;
  color?: string;
};

function ContrastChartTooltip({
  label,
  rows,
}: {
  label: string;
  rows: TooltipEntry[];
}) {
  if (!rows.length) return null;

  return (
    <div className="min-w-[180px] border border-border/80 bg-background/95 px-2.5 py-2 shadow-lg backdrop-blur-sm">
      <div className="font-mono text-[0.62rem] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1.5 space-y-1.5">
        {rows.map((row) => (
          <div key={`${row.label}-${row.value}`} className="flex items-center justify-between gap-2 text-xs">
            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
              {row.color ? <span className="inline-block h-2 w-2" style={{ backgroundColor: row.color }} /> : null}
              {row.label}
            </span>
            <span className="font-mono text-foreground">{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatPriceTick(value: number): string {
  return formatPrice(value);
}

function computePaddedDomain(values: number[]): [number, number] {
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min;

  if (span <= Number.EPSILON) {
    const pad = Math.max(Math.abs(min) * 0.01, 0.01);
    return [min - pad, max + pad];
  }

  const pad = span * 0.06;
  return [min - pad, max + pad];
}

function autoDomain(values: number[]): [number, number] | ["auto", "auto"] {
  if (!values.length) {
    return ["auto", "auto"];
  }
  return computePaddedDomain(values);
}

function lineValues(data: Array<{ t: string; value: number | null }>): number[] {
  return data
    .map((point) => point.value)
    .filter((value): value is number => typeof value === "number" && Number.isFinite(value));
}

function barValues(data: Array<{ provider: string; value: number | null }>): number[] {
  return data
    .map((point) => point.value)
    .filter((value): value is number => typeof value === "number" && Number.isFinite(value));
}

function stackedValues(
  data: Array<Record<string, number | string | null>>,
  providers: string[]
): number[] {
  if (!data.length) return [];

  return data.flatMap((row) => {
    const keys = providers.length
      ? providers
      : Object.keys(row).filter((key) => key !== "t");

    let cumulative = 0;
    const values: number[] = [0];

    for (const key of keys) {
      const value = row[key];
      const num = Number(value);
      if (!Number.isFinite(num)) continue;
      cumulative += num;
      values.push(cumulative);
    }

    return values;
  });
}

export function LineMetricChart({
  data,
  valueFormatter = (value) => formatCompact(value as number),
  axisFormatter,
  tooltipVariant = "default",
}: {
  data: Array<{ t: string; value: number | null }>;
  valueFormatter?: (value: number) => string;
  axisFormatter?: (value: number) => string;
  tooltipVariant?: "default" | "contrast";
}) {
  const domain = autoDomain(lineValues(data));

  return (
    <div className="h-[280px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ left: -8, right: 8, top: 12, bottom: 0 }}>
          <CartesianGrid stroke="var(--border)" strokeOpacity={0.35} vertical={false} />
          <XAxis
            dataKey="t"
            tickFormatter={(value) => formatUtcTick(value)}
            minTickGap={36}
            {...xAxisProps}
          />
          <YAxis
            width={68}
            domain={domain}
            tickFormatter={(value) =>
              axisFormatter ? axisFormatter(Number(value)) : formatCompact(value as number)
            }
            tick={axisTickStyle}
            axisLine={{ stroke: "var(--border)", strokeOpacity: 0.8 }}
            tickLine={{ stroke: "var(--border)", strokeOpacity: 0.8 }}
          />
          {tooltipVariant === "contrast" ? (
            <Tooltip
              content={({ active, label, payload }) => {
                if (!active || !payload?.length) return null;
                const point = payload[0] as RechartsTooltipPayload;
                const num = Number(point.value);

                return (
                  <ContrastChartTooltip
                    label={formatUtcTick(String(label))}
                    rows={[
                      {
                        label: "Value",
                        value: Number.isFinite(num) ? valueFormatter(num) : "—",
                        color: point.color,
                      },
                    ]}
                  />
                );
              }}
            />
          ) : (
            <Tooltip
              labelFormatter={(value) => formatUtcTick(String(value))}
              formatter={(value) => {
                const num = Number(value);
                return Number.isFinite(num) ? valueFormatter(num) : "—";
              }}
              contentStyle={{
                background: "var(--card)",
                border: "1px solid var(--border)",
              }}
            />
          )}
          <Line
            type="monotone"
            dataKey="value"
            stroke="var(--primary)"
            strokeWidth={2.2}
            dot={false}
            connectNulls
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export function StackedProviderAreaChart({
  data,
  providers,
  valueFormatter,
  tooltipVariant = "contrast",
}: {
  data: Array<Record<string, number | string | null>>;
  providers: string[];
  valueFormatter?: (value: number) => string;
  tooltipVariant?: "default" | "contrast";
}) {
  const domain = autoDomain(stackedValues(data, providers));

  return (
    <div className="h-[300px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ left: 0, right: 8, top: 12, bottom: 0 }}>
          <CartesianGrid stroke="var(--border)" strokeOpacity={0.35} vertical={false} />
          <XAxis
            dataKey="t"
            tickFormatter={(value) => formatUtcTick(String(value))}
            minTickGap={36}
            {...xAxisProps}
          />
          <YAxis
            width={80}
            domain={domain}
            tickFormatter={(value) =>
              valueFormatter
                ? valueFormatter(Number(value))
                : `${formatNumber(Number(value) * 100, 1)}%`
            }
            tick={axisTickStyle}
            axisLine={{ stroke: "var(--border)", strokeOpacity: 0.8 }}
            tickLine={{ stroke: "var(--border)", strokeOpacity: 0.8 }}
          />
          {tooltipVariant === "contrast" ? (
            <Tooltip
              content={({ active, label, payload }) => {
                if (!active || !payload?.length) return null;

                const rows = payload.reduce<TooltipEntry[]>((acc, entry) => {
                  const point = entry as RechartsTooltipPayload;
                  const num = Number(point.value);
                  if (!Number.isFinite(num)) return acc;

                  acc.push({
                    label: String(point.name ?? "Value"),
                    value: valueFormatter ? valueFormatter(num) : formatPercent(num * 100),
                    color: point.color,
                  });

                  return acc;
                }, []);

                return <ContrastChartTooltip label={formatUtcTick(String(label))} rows={rows} />;
              }}
            />
          ) : (
            <Tooltip
              labelFormatter={(value) => formatUtcTick(String(value))}
              formatter={(value) => {
                const num = Number(value);
                if (!Number.isFinite(num)) return "—";
                if (valueFormatter) return valueFormatter(num);
                return formatPercent(num * 100);
              }}
              contentStyle={{
                background: "var(--card)",
                border: "1px solid var(--border)",
              }}
            />
          )}

          {providers.map((provider, index) => (
            <Area
              key={provider}
              dataKey={provider}
              stackId="providers"
              type="monotone"
              stroke={palette[index % palette.length]}
              fill={palette[index % palette.length]}
              fillOpacity={0.35}
              connectNulls
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function ProviderBarMetricChart({
  data,
  valueFormatter,
}: {
  data: Array<{ provider: string; value: number | null }>;
  valueFormatter?: (value: number) => string;
}) {
  const domain = autoDomain(barValues(data));

  return (
    <div className="h-[280px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ left: 0, right: 8, top: 12, bottom: 0 }}>
          <CartesianGrid stroke="var(--border)" strokeOpacity={0.35} vertical={false} />
          <XAxis dataKey="provider" {...xAxisProps} />
          <YAxis
            width={80}
            domain={domain}
            tickFormatter={(value) => formatCompact(value as number)}
            tick={axisTickStyle}
            axisLine={{ stroke: "var(--border)", strokeOpacity: 0.8 }}
            tickLine={{ stroke: "var(--border)", strokeOpacity: 0.8 }}
          />
          <Tooltip
            content={({ active, label, payload }) => {
              if (!active || !payload?.length) return null;
              const point = payload[0] as RechartsTooltipPayload & {
                payload?: { provider?: string };
              };
              const num = Number(point.value);
              const providerLabel =
                typeof label === "string" && label.trim()
                  ? label
                  : point.payload?.provider ?? "Provider";

              return (
                <ContrastChartTooltip
                  label={providerLabel.toUpperCase()}
                  rows={[
                    {
                      label: "Value",
                      value: Number.isFinite(num)
                        ? valueFormatter
                          ? valueFormatter(num)
                          : formatCompact(num)
                        : "—",
                      color: point.color,
                    },
                  ]}
                />
              );
            }}
          />
          <Bar dataKey="value" radius={[0, 0, 0, 0]}>
            {data.map((_, index) => (
              <Cell key={`bar-${index}`} fill={palette[index % palette.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function PriceLineChart({
  data,
  providerLabel,
}: {
  data: Array<{ t: string; value: number | null }>;
  providerLabel?: string;
}) {
  const domain = autoDomain(lineValues(data));

  return (
    <div className="h-[280px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ left: -8, right: 8, top: 12, bottom: 0 }}>
          <CartesianGrid stroke="var(--border)" strokeOpacity={0.35} vertical={false} />
          <XAxis
            dataKey="t"
            tickFormatter={(value) => formatUtcTick(String(value))}
            minTickGap={36}
            {...xAxisProps}
          />
          <YAxis
            width={74}
            domain={domain}
            tickFormatter={(value) => `$${formatPriceTick(Number(value))}`}
            tick={axisTickStyle}
            axisLine={{ stroke: "var(--border)", strokeOpacity: 0.8 }}
            tickLine={{ stroke: "var(--border)", strokeOpacity: 0.8 }}
          />
          <Tooltip
            content={({ active, label, payload }) => {
              if (!active || !payload?.length) return null;
              const point = payload[0] as RechartsTooltipPayload;
              const num = Number(point.value);

              const rows: TooltipEntry[] = [
                {
                  label: "Price",
                  value: Number.isFinite(num) ? `$${formatPriceTick(num)}` : "—",
                  color: point.color,
                },
              ];

              if (providerLabel) {
                rows.push({
                  label: "Provider",
                  value: providerLabel,
                });
              }

              return <ContrastChartTooltip label={formatUtcTick(String(label))} rows={rows} />;
            }}
          />
          <Line
            type="monotone"
            dataKey="value"
            stroke="var(--primary)"
            strokeWidth={2.2}
            dot={false}
            connectNulls
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export function CandlestickChart({
  data,
}: {
  data: Array<{ t: string; open: number | null; high: number | null; low: number | null; close: number | null }>;
}) {
  const chartData = data
    .filter(
      (point) =>
        point.open != null && point.high != null && point.low != null && point.close != null
    )
    .map((point) => {
      const open = point.open as number;
      const close = point.close as number;
      const high = point.high as number;
      const low = point.low as number;
      const bodyBase = Math.min(open, close);
      const bodySize = Math.max(Math.abs(close - open), Number.EPSILON);
      const mid = (high + low) / 2;
      const wickRange: [number, number] = [mid - low, high - mid];

      return {
        t: point.t,
        open,
        close,
        high,
        low,
        bodyBase,
        bodySize,
        wickMid: mid,
        wickRange,
        rising: close >= open,
      };
    });

  const wickValues = chartData.flatMap((entry) => [entry.low, entry.high]);
  const domain = autoDomain(wickValues);

  return (
    <div className="h-[320px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={chartData} margin={{ left: 0, right: 8, top: 12, bottom: 0 }}>
          <CartesianGrid stroke="var(--border)" strokeOpacity={0.35} vertical={false} />
          <XAxis
            dataKey="t"
            tickFormatter={(value) => formatUtcTick(String(value))}
            minTickGap={36}
            {...xAxisProps}
          />
          <YAxis
            width={80}
            domain={domain}
            tickFormatter={(value) => formatPrice(Number(value))}
            tick={axisTickStyle}
            axisLine={{ stroke: "var(--border)", strokeOpacity: 0.8 }}
            tickLine={{ stroke: "var(--border)", strokeOpacity: 0.8 }}
          />
          <Tooltip
            labelFormatter={(value) => formatUtcTick(String(value))}
            formatter={(value: unknown, name) => {
              if (name === "open" || name === "high" || name === "low" || name === "close") {
                return `$${formatPrice(Number(value))}`;
              }
              return String(value);
            }}
            contentStyle={{
              background: "var(--card)",
              border: "1px solid var(--border)",
            }}
          />

          <Bar dataKey="bodyBase" stackId="candles" fill="transparent" isAnimationActive={false} />
          <Bar dataKey="bodySize" stackId="candles" radius={[0, 0, 0, 0]} isAnimationActive={false}>
            {chartData.map((entry, index) => (
              <Cell key={`candle-${index}`} fill={entry.rising ? "#22c55e" : "#fb7185"} />
            ))}
          </Bar>

          <Scatter dataKey="wickMid" fill="transparent" isAnimationActive={false} shape={() => null}>
            <ErrorBar dataKey="wickRange" width={0} stroke="var(--foreground)" strokeWidth={1} />
          </Scatter>
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
